import React, { useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  EndStep,
  Flow,
  FlowStep,
  MessageStep,
  RouteStep,
} from "../../../../types";

// ─── RouteStepForm ───────────────────────────────────────────────────────────

const routeSchema = z.object({
  next_flow_id: z.string().min(1, "Seleziona un flow"),
  order: z.number().int().min(0),
});
type RouteForm = z.infer<typeof routeSchema>;

interface RouteProps {
  step: RouteStep;
  flows: Flow[];
  onSave: (updated: RouteStep) => void;
}

export const RouteStepForm = ({ step, flows, onSave }: RouteProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RouteForm>({
    resolver: zodResolver(routeSchema),
    defaultValues: { next_flow_id: step.next_flow_id, order: step.order },
  });

  useEffect(() => {
    reset({ next_flow_id: step.next_flow_id, order: step.order });
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit((data) =>
    onSave({ ...step, next_flow_id: data.next_flow_id, order: data.order })
  );

  return (
    <form onSubmit={onSubmit} className="flex-col gap-2">
      <Controller
        name="next_flow_id"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small" error={!!errors.next_flow_id}>
            <InputLabel>Flow destinazione</InputLabel>
            <Select {...field} label="Flow destinazione" value={field.value ?? ""}>
              <MenuItem value="">— nessuno —</MenuItem>
              {flows.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <Box>
        <Button variant="contained" type="submit" size="small">
          Salva step
        </Button>
      </Box>
    </form>
  );
};

// ─── MessageStepForm ─────────────────────────────────────────────────────────

const messageSchema = z.object({
  content: z.string().min(1, "Contenuto obbligatorio"),
  next_step_id: z.string().optional(),
  order: z.number().int().min(0),
});
type MessageForm = z.infer<typeof messageSchema>;

interface MessageProps {
  step: MessageStep;
  steps: FlowStep[];
  onSave: (updated: MessageStep) => void;
}

export const MessageStepForm = ({ step, steps, onSave }: MessageProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MessageForm>({
    resolver: zodResolver(messageSchema),
    defaultValues: {
      content: step.content,
      next_step_id: step.next_step_id ?? "",
      order: step.order,
    },
  });

  useEffect(() => {
    reset({
      content: step.content,
      next_step_id: step.next_step_id ?? "",
      order: step.order,
    });
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit((data) =>
    onSave({
      ...step,
      content: data.content,
      next_step_id: data.next_step_id || undefined,
      order: data.order,
    })
  );

  return (
    <form onSubmit={onSubmit} className="flex-col gap-2">
      <Controller
        name="content"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Testo del messaggio"
            error={!!errors.content}
            helperText={errors.content?.message}
          />
        )}
      />
      <Controller
        name="next_step_id"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel>Step successivo (opzionale)</InputLabel>
            <Select
              {...field}
              label="Step successivo (opzionale)"
              value={field.value ?? ""}
            >
              <MenuItem value="">— nessuno —</MenuItem>
              {steps
                .filter((s) => s.id !== step.id)
                .map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    [{s.type}] {s.id.slice(0, 8)}… (ord. {s.order})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      />
      <Box>
        <Button variant="contained" type="submit" size="small">
          Salva step
        </Button>
      </Box>
    </form>
  );
};

// ─── EndStepForm ─────────────────────────────────────────────────────────────

const endSchema = z.object({
  content: z.string().min(1, "Contenuto obbligatorio"),
  order: z.number().int().min(0),
});
type EndForm = z.infer<typeof endSchema>;

interface EndProps {
  step: EndStep;
  onSave: (updated: EndStep) => void;
}

export const EndStepForm = ({ step, onSave }: EndProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EndForm>({
    resolver: zodResolver(endSchema),
    defaultValues: { content: step.content, order: step.order },
  });

  useEffect(() => {
    reset({ content: step.content, order: step.order });
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit((data) =>
    onSave({ ...step, content: data.content, order: data.order })
  );

  return (
    <form onSubmit={onSubmit} className="flex-col gap-2">
      <Controller
        name="content"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Messaggio di chiusura"
            error={!!errors.content}
            helperText={errors.content?.message}
          />
        )}
      />
      <Box>
        <Button variant="contained" type="submit" size="small">
          Salva step
        </Button>
      </Box>
    </form>
  );
};
